import { type InfraScheduleItem } from '@carsfinders/contracts';
import { CheckCircle2 } from 'lucide-react';

import { t } from '../../../lib/i18n';
import { cn } from '../../clients/utils';
import { amountWithUsd, formatDate, scheduleStatusLabel } from '../format';

const BADGE: Record<InfraScheduleItem['status'], string> = {
  overdue: 'bg-cf-danger-tint text-cf-danger',
  due_soon: 'bg-cf-warn-tint text-cf-ink',
  upcoming: 'bg-cf-fill text-cf-ink-muted',
};

/**
 * «Найближчі платежі» — `summary.upcoming`: the overdue ones first (the server
 * sorts by date), each with its own-currency amount, the USD estimate when it
 * is another currency, a status badge and «Оплачено».
 *
 * «Оплачено» sits on a service's FIRST row only. Recording a payment settles
 * the service's current `nextDueDate` — which is that first row, the list being
 * date-sorted — so a button on a later projected row (Atlas 01.12) would open a
 * dialog naming 01.12 and then settle 01.11.
 */
export function InfraUpcomingList({
  items,
  onPay,
}: {
  items: readonly InfraScheduleItem[];
  onPay: (item: InfraScheduleItem) => void;
}) {
  if (items.length === 0) {
    return <p className="py-6 text-center text-[13px] text-cf-ink-subtle">{t('admin.infraCosts.upcoming.empty')}</p>;
  }

  const payable = new Set<string>();
  const seen = new Set<string>();
  for (const item of items) {
    if (seen.has(item.serviceId)) continue;
    seen.add(item.serviceId);
    payable.add(`${item.serviceId}:${item.dueDate}`);
  }

  return (
    <ul className="flex flex-col divide-y divide-cf-line-soft" data-testid="infra-upcoming">
      {items.map((item) => {
        const amount = amountWithUsd(item);
        const canPay = payable.has(`${item.serviceId}:${item.dueDate}`);
        return (
          <li
            key={`${item.serviceId}:${item.dueDate}`}
            data-status={item.status}
            className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2.5"
          >
            <span className="w-[86px] shrink-0 font-mono text-[13px] text-cf-ink">{formatDate(item.dueDate)}</span>
            <span className="min-w-[120px] flex-1 text-[14px] font-semibold text-cf-ink">{item.serviceName}</span>
            <span className="flex flex-col items-end text-right">
              <span className="font-mono text-[14px] text-cf-ink">{amount.own}</span>
              {amount.usd && <span className="font-mono text-[11px] text-cf-ink-subtle">{amount.usd}</span>}
            </span>
            <span
              data-testid="infra-status-badge"
              className={cn('rounded-full px-2.5 py-0.5 text-[12px] font-semibold whitespace-nowrap', BADGE[item.status])}
            >
              {scheduleStatusLabel(item)}
            </span>
            {canPay ? (
              <button
                type="button"
                onClick={() => onPay(item)}
                aria-label={t('admin.infraCosts.upcoming.payAria', {
                  service: item.serviceName,
                  date: formatDate(item.dueDate),
                })}
                className="inline-flex h-9 items-center gap-1.5 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[13px] font-semibold text-cf-ink hover:border-cf-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink"
              >
                <CheckCircle2 size={15} aria-hidden="true" />
                {t('admin.infraCosts.upcoming.pay')}
              </button>
            ) : (
              // Keeps the row's columns aligned with the payable rows above it.
              <span aria-hidden="true" className="inline-block h-9 w-[104px]" />
            )}
          </li>
        );
      })}
    </ul>
  );
}
